import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { AppDataService } from '@services/app-data.service';
import { CommonUtilityService } from '@services/common-utility.service';
import { IGuestsResponse, MsGuestsService } from '@upr-web-primer/ms-guests';
import { NotificationService } from '@upr-web-primer/notification';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { get } from '@upr-web-primer/udx-utils';

interface ChangePasswordResponse {
  body?: {
    message?: string;
  };
}

@Component({
  selector: 'app-update-password',
  templateUrl: './update-password.component.html',
  styleUrls: ['./update-password.component.scss'],
  standalone: false,
})

/**
 * Update password component
 */
export class UpdatePasswordComponent implements OnInit, AfterViewInit {
  /**
   * Page labels
   * @type any
   */
  pageLabels: any;

  /**
   * Update password form
   * @type FormGroup
   */
  updatePasswordForm: FormGroup;
  strongPassword = false;
  passWordErrorMsgInfo: any;
  newPassWordErrorMsgInfo: any;
  rePassWordErrorMsgInfo: any;
  user_id: string | undefined;
  showReEnterPassword = environment.config?.showReEnterPassword;
  @ViewChild('updatePwdModalContent') updatePwdModalContent!: ElementRef;
  lastErrorMsg: any = {};

  /**
   * Constructor
   * @param {ActivatedRoute} route
   * @param {CommonUtilityService} commonUtilityService
   * @param {MsGuestsService} guestsService
   * @param {FormBuilder} fb
   * @param {NotificationService} notification
   * @param {any} appRoutes
   */
  constructor(
    private readonly commonUtilityService: CommonUtilityService,
    private readonly guestsService: MsGuestsService,
    private readonly fb: FormBuilder,
    private readonly notification: NotificationService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly appDataService: AppDataService,
    @Inject('GlobalDefaults') public globalDefault: any,
    @Inject('AppRoutes') public appRoutes: any,
    @Inject('Ranges') private readonly ranges: any,
    private readonly title: Title,
    @Inject('TriggerDDLMappings') public triggerDDLMappings: any,
    private readonly spinner: UdxSpinnerService,
  ) {
    if (this.commonUtilityService?.getResolvedPageData) {
      this.pageLabels = this.commonUtilityService.getResolvedPageData();
      this.title.setTitle(this.pageLabels?.['editPwd.info1']);
    }
  }

  /**
   * NgOnInit - initial call to methods initform and loadUserInfo
   */
  ngOnInit(): void {
    this.notification.hide();
    this.initForm();
    this.loadUserInfo();
    this.commonUtilityService.triggerModalDDLEvent(
      this.pageLabels?.['editPwd.heading'],
      true,
    );
  }

  ngAfterViewInit(): void {
    this.appDataService.modalContentHeightCalc(this.updatePwdModalContent, 164);
  }

  @HostListener('window:resize')
  onResize(): any {
    this.appDataService.modalContentHeightCalc(this.updatePwdModalContent, 164);
  }
  /**
   * Load user info - To fetch user details
   * @returns {any}
   */
  loadUserInfo(ignoreUserName?: boolean): Promise<boolean> {
    this.spinner.show();
    if (!ignoreUserName && !this.guestsService.helper.isAuth()) {
      return Promise.resolve(false);
    }
    return this.guestsService.profile
      .getByGuestId(this.guestsService.helper.getGuestId())
      .then((userData: IGuestsResponse) => {
        this.spinner.hide();
        this.user_id = userData?.user_id;
        return true;
      })
      .catch((error: any) => {
        if (error.status == this.globalDefault.ERROR_401) {
          this.commonUtilityService.handleUnauthorizedError();
        }
        this.spinner.hide();
        return false;
      });
  }

  /**
   * OnSubmit - check the action on button based on that call the function
   */
  onSubmit(event: any): any {
    if (event.target?.data.action === 'back') {
      this.goToProfile();
    } else if (event.target?.data.action === 'update') {
      this.updatePassword();
    }
  }

  /**
   * updatePassword - service call on click of update button
   */
  updatePassword(): any {
    const existingPwd = get(this.updatePasswordForm, 'value.passWord');
    const newPwd = get(this.updatePasswordForm, 'value.newPassWord');

    if (
      !localStorage.oAuthLoginUserName ||
      newPwd === localStorage.oAuthLoginUserName
    ) {
      return this.notification.show(this.pageLabels?.ER60);
    }
    this.notification.hide();
    const request = {
      oldPassword: existingPwd,
      newPassword: newPwd,
    };

    this.commonUtilityService.changePassword(this.user_id, request).subscribe(
      (response: ChangePasswordResponse) => {
        this.commonUtilityService.triggerAccountUpdateDDLEvent(
          this.pageLabels?.['editPwd.heading'],
        );
        const hasResponseMessage =
          typeof response?.body?.message === 'string' &&
          response.body.message.trim().length > 0;
        const message =
          this.pageLabels?.['editPwd.success'] ??
          (hasResponseMessage ? response?.body?.message : undefined);
        this.notification.show(message, 'success');
        this.goToProfile();
      },

      (err: any) => {
        this.commonUtilityService.triggerSystemErrorDDLEvent(err);
        this.notification.hide();
        if (err === this.globalDefault.ERROR_ER71) {
          this.passWordErrorMsgInfo = this.pageLabels?.['errCode.MS.pwd_400'];
        } else if (err == this.globalDefault.ERROR_401) {
          this.commonUtilityService.handleUnauthorizedError();
        } else {
          this.passWordErrorMsgInfo = this.showReEnterPassword
            ? this.pageLabels?.['errCode.E019"']
            : this.pageLabels?.['errCode.MS.pwd_400'];
        }
        if (err !== this.globalDefault.ERROR_401) {
          this.notification.show(
            this.passWordErrorMsgInfo ??
              this.pageLabels?.['errCode.MS.pwd_400'],
            'error',
          );
        }
      },
    );
  }

  /**
   * initForm - update form created
   */
  private initForm(): void {
    this.updatePasswordForm = this.fb.group({
      passWord: new FormControl('', [Validators.required]),
      newPassWord: new FormControl('', [
        Validators.required,
        this.showReEnterPassword
          ? this.commonUtilityService.passwordValidator
          : Validators.nullValidator,
      ]),
      rePassWord: new FormControl('', [
        this.showReEnterPassword
          ? Validators.required
          : Validators.nullValidator,
        this.showReEnterPassword
          ? this.commonUtilityService.passwordValidator
          : Validators.nullValidator,
      ]),
    });
  }

  /**
   * onPasswordStrengthChanged - emiied from password strength component to get the strength of password
   * @param event
   */
  onPasswordStrengthChanged(event: boolean): void {
    this.strongPassword = event;
    this.cdRef.markForCheck();
  }

  /**
   * goToProfile - navigate to profile page
   */

  goToProfile(): void {
    this.commonUtilityService.changeRoute(this.appRoutes.MY_PROFILE);
    this.commonUtilityService.triggerModalDDLEvent(
      this.pageLabels?.['editPwd.heading'],
      false,
    );
  }

  /**
   * getErrorMessageInfo - Validation for all the fields
   * @param event
   */

  getErrorMessageInfo(event: any): void {
    const fieldInfo = event?.target?.id;
    let errorMsg = '';
    switch (fieldInfo) {
      case this.globalDefault.PASSWORD:
        this.passWordErrorMsgInfo = this.getErrorMessage(fieldInfo);
        errorMsg = this.passWordErrorMsgInfo;
        break;
      case this.globalDefault.NEWPASSWRD:
        this.newPassWordErrorMsgInfo = this.getErrorMessage(fieldInfo);
        errorMsg = this.newPassWordErrorMsgInfo;
        break;
      case this.globalDefault.REPASSWORD:
        this.rePassWordErrorMsgInfo = this.getErrorMessage(fieldInfo);
        break;
    }
    if (errorMsg && this.lastErrorMsg[fieldInfo] !== errorMsg) {
      this.commonUtilityService.triggerDDLEventHandler(
        this.triggerDDLMappings.INLINE_ERROR,
        {
          isError: true,
          errorMessage: errorMsg,
        },
      );
    }

    this.lastErrorMsg[fieldInfo] = errorMsg;
    this.cdRef.markForCheck();
  }

  /**
   * getErrorMessage - To get error meassges for all the fields
   * @param field
   * @returns errorMessage
   */

  getErrorMessage(field: any): any {
    switch (field) {
      case this.globalDefault.PASSWORD:
        return this.validatePassword(
          this.updatePasswordForm.value.passWord,
          this.globalDefault.PASSWORD,
        );

      case this.globalDefault.NEWPASSWRD:
        if (this.showReEnterPassword) {
          return this.validatePassword(
            this.updatePasswordForm.value.newPassWord,
            this.globalDefault.NEWPASSWRD,
          );
        } else {
          const emptyCheck = this.pageLabels?.['errCode.E001'];
          if (!this.updatePasswordForm.value.newPassWord) {
            if (this.globalDefault.NEWPASSWRD === this.globalDefault.PASSWORD)
              return (this.passWordErrorMsgInfo = emptyCheck);
            else return (this.newPassWordErrorMsgInfo = emptyCheck);
          } else if (
            this.updatePasswordForm.value.newPassWord ===
            this.updatePasswordForm.value.passWord
          ) {
            return this.pageLabels?.['errCode.E030'];
          } else if (!this.strongPassword) {
            return this.pageLabels?.['errCode.E005'];
          }
          return '';
        }

      case this.globalDefault.REPASSWORD: {
        if (!this.updatePasswordForm.value.rePassWord) {
          return this.pageLabels?.['errCode.E001'];
        } else if (
          this.updatePasswordForm.value.newPassWord !==
          this.updatePasswordForm.value.rePassWord
        ) {
          return this.pageLabels?.['errCode.E012'];
        }
      }
    }
    return '';
  }

  /**
   *  validatePassword - to validate the enter value as password
   * @param value - enetered value
   * @returns
   */

  validatePassword(value: any, result: any): any {
    const emptyCheck = this.pageLabels?.['errCode.E001'];
    const spaceCheck = this.pageLabels?.['errCode.E033'];

    // Check if the value is empty
    if (!value) {
      if (result === this.globalDefault.PASSWORD) {
        return (this.passWordErrorMsgInfo = emptyCheck);
      } else {
        return (this.newPassWordErrorMsgInfo = emptyCheck);
      }
    }

    // Check for password strength if required
    if (!this.strongPassword && result === this.globalDefault.NEWPASSWRD) {
      return (this.newPassWordErrorMsgInfo = this.pageLabels?.['errCode.E005']);
    }
    if (result === this.globalDefault.NEWPASSWRD) {
      // Check for special characters

      // Check for spaces
      if (/\s/.test(value)) {
        return (this.newPassWordErrorMsgInfo = spaceCheck);
      }

      // If all checks pass
      this.newPassWordErrorMsgInfo = '';
    }

    if (result === this.globalDefault.REPASSWORD) {
      const newPassWord = this.updatePasswordForm?.value?.newPassWord || '';
      // Check if passwords match
      if (value === newPassWord) {
        this.rePassWordErrorMsgInfo = '';
      } else {
        this.rePassWordErrorMsgInfo = this.pageLabels?.['errCode.E012'];
      }

      // Check for spaces in re-entered password
      if (/\s/.test(value)) {
        this.rePassWordErrorMsgInfo = spaceCheck;
      }
    }
  }

  /**
   * redirectToLogin - redirect to login
   */
  redirectToLogin(): void {
    const data = {
      response_Type: this.globalDefault.RESPONSE_TYPE,
      scope: this.globalDefault.SCOPE,
      state: this.globalDefault.WEB,
      nonce: this.generateSecureNonce(
        this.ranges.STRING.LENGTH,
        this.ranges.PASSWORDS.INDEX[7],
      ),
    };

    this.commonUtilityService.redirectToLogin(data);
  }

  /**
   * Generates a cryptographically secure nonce string.
   * @param baseLength The numeric base to use for conversion (e.g. 36 for alphanumeric).
   * @param trimStart The start index for trimming the resulting string.
   * @returns A secure, trimmed nonce string.
   */
  generateSecureNonce(baseLength?: number, trimStart?: number): string {
    const baseLen = baseLength ?? this.globalDefault.NONCE_BASE_LENGTH;
    const trimIdx = trimStart ?? this.globalDefault.NONCE_TRIM_START;

    const array = new Uint8Array(this.globalDefault.NONCE_BYTE_LENGTH);
    crypto.getRandomValues(array);

    const baseString = Array.from(array)
      .map((byte) => byte.toString(baseLen))
      .join('');
    return baseString.substring(trimIdx);
  }

  /**
   * checkEnterVal -  to verify the newpassword and rePassword fields input
   */
  checkEnterVal(): any {
    if (this.showReEnterPassword) {
      const newPassWord = this.updatePasswordForm?.value?.newPassWord || '';
      const rePassWord = this.updatePasswordForm?.value?.rePassWord || '';

      // Check if passwords match
      this.rePassWordErrorMsgInfo =
        newPassWord === rePassWord ? '' : this.pageLabels?.['errCode.E012'];

      // Check for spaces in the new password
      this.newPassWordErrorMsgInfo = /\s/.test(newPassWord)
        ? this.pageLabels?.['errCode.E033']
        : '';

      // Check for spaces in the re-entered password
      if (/\s/.test(rePassWord)) {
        this.rePassWordErrorMsgInfo = this.pageLabels?.['errCode.E033'];
      }
      // if (
      //   this.updatePasswordForm?.value?.rePassWord ===
      //   this.updatePasswordForm?.value?.newPassWord
      // )
      //   this.rePassWordErrorMsgInfo = '';
      // else this.rePassWordErrorMsgInfo = this.pageLabels?.['errCode.E012'];
    }
  }

  /**
   * disableUpdateButton -  to disable the button based on the formValidation and flag set in enviornment
   */
  enableUpdateButton(): boolean {
    if (this.showReEnterPassword)
      return (
        this.updatePasswordForm?.valid &&
        this.updatePasswordForm.value.newPassWord ===
          this.updatePasswordForm.value.rePassWord &&
        this.strongPassword &&
        this.updatePasswordForm.value.passWord !==
          this.updatePasswordForm.value.newPassWord
      );
    else
      return (
        this.updatePasswordForm?.valid &&
        this.strongPassword &&
        this.updatePasswordForm.value.passWord !==
          this.updatePasswordForm.value.newPassWord
      );
  }

  /**
   * redirectToForget -  To redirect forgot password oidc page
   */
  redirectToForget(): void {
    const redirectUrl = this.commonUtilityService.getOidcRedirectionUrl();
    this.commonUtilityService.redirectToOidcForget(redirectUrl);
  }
}
